import { apiClient } from "../../lib/apiClient";
import {
  ManagedStoreRow,
  ProvisionJobLog,
  ProvisionJobSummary,
  RolesByCatalogStore,
  StorePatchResponse,
  UploadProvisionCsvPayload
} from "./types";

const PROVISION_ROOT = "/provision";

export const fetchProvisionJobs = async (scope?: "all" | "mine"): Promise<ProvisionJobSummary[]> => {
  const response = await apiClient.get<ProvisionJobSummary[]>(`${PROVISION_ROOT}/`, {
    params: scope ? { scope } : undefined
  });
  return response.data;
};

export const fetchProvisionJob = async (jobId: string): Promise<ProvisionJobSummary> => {
  const response = await apiClient.get<ProvisionJobSummary>(`${PROVISION_ROOT}/${jobId}`);
  return response.data;
};

export const fetchProvisionJobLogs = async (jobId: string): Promise<ProvisionJobLog[]> => {
  const response = await apiClient.get<ProvisionJobLog[]>(`${PROVISION_ROOT}/${jobId}/logs`);
  return response.data;
};

/**
 * Issue fresh temp passwords for every user whose credentials email bounced
 * during this job. Returns the CSV body as a Blob; the caller triggers the
 * browser download. Plaintext only exists in this response — never stored.
 */
export const bulkResetFailedCredentials = async (jobId: string): Promise<Blob> => {
  const response = await apiClient.post<Blob>(
    `${PROVISION_ROOT}/${jobId}/credentials/bulk-reset`,
    undefined,
    { responseType: "blob" }
  );
  return response.data;
};

export const fetchMyManagedStores = async (): Promise<ManagedStoreRow[]> => {
  const response = await apiClient.get<ManagedStoreRow[]>("/users/me/managed-stores");
  return response.data;
};

export interface StoreProvisioningPatch {
  provisioning_method?: "csv" | "self_registration";
  is_active?: boolean;
}

/** PATCH /api/v1/stores/{store_id} — provision Lambda only. */
export const patchStoreProvisioning = async (
  storeId: string,
  body: StoreProvisioningPatch
): Promise<StorePatchResponse> => {
  const response = await apiClient.patch<StorePatchResponse>(
    `/stores/${encodeURIComponent(storeId)}`,
    body
  );
  return response.data;
};

export const fetchMargaritavilleRolesByStore = async (storeId: string): Promise<RolesByCatalogStore> => {
  const response = await apiClient.get<RolesByCatalogStore>("/catalog/margaritaville/roles-by-store", {
    params: { store_id: storeId }
  });
  return response.data;
};

/** POST returns 202 with the just-queued job. Worker processes rows async; */
/** the operator polls /provision/ for progress and retrieves failed-creds via the bulk-reset endpoint. */
export const uploadProvisionCsv = async ({
  file,
  name,
  description,
  onUploadProgress
}: UploadProvisionCsvPayload): Promise<{ job: ProvisionJobSummary }> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", name);
  if (description) {
    formData.append("description", description);
  }

  const response = await apiClient.post<ProvisionJobSummary>(
    `${PROVISION_ROOT}/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      },
      onUploadProgress: (progressEvent) => {
        if (!onUploadProgress) {
          return;
        }
        const loaded = progressEvent.loaded;
        const reported = progressEvent.total ?? 0;
        const total = reported > 0 ? reported : file.size;
        const percent = total > 0 ? Math.min(100, Math.round((loaded * 100) / total)) : null;
        onUploadProgress({ loaded, total, percent });
      }
    }
  );

  return { job: response.data };
};
